import {
  DiscoveryApi,
  FetchApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import {
  PortalgroupsRbacAuthorization,
  portalgroupsReadPermission,
  portalgroupsCreatePermission,
  portalgroupsUpdatePermission,
  portalgroupsDeletePermission,
  type GroupInput,
  type PortalGroup,
} from '@internal/backstage-plugin-portalgroups-common';
import { PortalgroupsApi, Permissions } from './PortalgroupsApi';

export class PortalgroupsClient implements PortalgroupsApi {
  readonly #discoveryApi: DiscoveryApi;
  readonly #fetchApi: FetchApi;
  readonly #identityApi: IdentityApi;
  readonly #rbacAuth = new PortalgroupsRbacAuthorization();

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    identityApi: IdentityApi;
  }) {
    this.#discoveryApi = options.discoveryApi;
    this.#fetchApi = options.fetchApi;
    this.#identityApi = options.identityApi;
  }

  private async baseUrl(): Promise<string> {
    return this.#discoveryApi.getBaseUrl('portalgroups');
  }

  /** Extracts a readable message from a Backstage error response body. */
  private extractErrorMessage(body: any, fallback: string): string {
    if (typeof body.error === 'string') return body.error;
    if (typeof body.error?.message === 'string') return body.error.message;
    return fallback;
  }

  async checkPermissions(): Promise<Permissions> {
    const { token = '' } = await this.#identityApi.getCredentials();
    return {
      read: this.#rbacAuth.hasPermission(token, portalgroupsReadPermission),
      create: this.#rbacAuth.hasPermission(token, portalgroupsCreatePermission),
      update: this.#rbacAuth.hasPermission(token, portalgroupsUpdatePermission),
      delete: this.#rbacAuth.hasPermission(token, portalgroupsDeletePermission),
    };
  }

  async fetchMyGroups(): Promise<PortalGroup[]> {
    const resp = await this.#fetchApi.fetch(
      `${await this.baseUrl()}/my-groups`,
    );
    if (!resp.ok) {
      throw new Error(`Failed to fetch groups: ${resp.statusText}`);
    }
    return resp.json();
  }

  async createGroup(input: GroupInput): Promise<PortalGroup> {
    const resp = await this.#fetchApi.fetch(`${await this.baseUrl()}/group`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      throw new Error(
        this.extractErrorMessage(
          body,
          `Failed to create group: ${resp.statusText}`,
        ),
      );
    }
    return resp.json();
  }

  async updateGroup(input: GroupInput): Promise<PortalGroup> {
    const resp = await this.#fetchApi.fetch(`${await this.baseUrl()}/group`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      throw new Error(
        this.extractErrorMessage(
          body,
          `Failed to update group: ${resp.statusText}`,
        ),
      );
    }
    return resp.json();
  }
}
